import { useMemo, useRef, useState } from 'react';

import { css, styled, useForkRef } from '@mui/material';
import { unstable_useEnhancedEffect as useEnhancedEffect } from '@mui/material/utils';

import { KeyValuePairItem as _KeyValuePairItem } from '../KeyValuePairItem';
import { KeyValuePairsContainer } from '../KeyValuePairsContainer';
import { Label } from '../Label';
import { keyValuePairsClasses, KeyValuePairSize } from './theme';
import { getAvailableColumnCount, packItemsIntoRows } from './utils';

import type { KeyValuePairsOwnerState, KeyValuePairsProps } from './types';

interface KeyValuePairsSpanGridOwnerState {
  columnCount: number;
  labelPosition: KeyValuePairsOwnerState['labelPosition'];
}

interface KeyValuePairsPositionedItemOwnerState extends KeyValuePairsOwnerState {
  columnSpan?: number;
  columnStart?: number;
  rowIndex?: number;
}

interface KeyValuePairsDividerOwnerState {
  rowIndex: number;
  columnSpan?: number;
  columnStart?: number;
}

interface KeyValuePairsTitleProps {
  ownerState: KeyValuePairsOwnerState;
  title: NonNullable<KeyValuePairsProps['title']>;
}

export default function KeyValuePairs(props: KeyValuePairsProps) {
  const {
    title,
    items,
    labelPosition = 'left',
    minColumnWidth = KeyValuePairSize.COLUMN_MIN_WIDTH,
    maxColumnRepeat = 2,
    showDivider = false,
    separateColumns = false,
    keySize = 'md',
    rowSize = 'md',
    showSideMargin = false,
    align = 'baseline',
    ref,
    ...others
  } = props;

  const [availableColumnCount, setAvailableColumnCount] = useState(1);
  const rootRef = useRef<HTMLDivElement>(null);

  const hasColumnSpan = useMemo(() => items.some(item => item.columnSpan !== undefined), [items]);
  const usesRowLayout = hasColumnSpan || showDivider;
  const packedRows = useMemo(
    () => (usesRowLayout ? packItemsIntoRows(items, availableColumnCount) : []),
    [availableColumnCount, items, usesRowLayout],
  );
  const forkedRef = useForkRef(ref, rootRef);

  const ownerState: KeyValuePairsOwnerState = {
    ...props,
    labelPosition,
    minColumnWidth,
    maxColumnRepeat,
    showDivider,
    keySize,
    rowSize,
  };

  useEnhancedEffect(() => {
    const element = rootRef.current;
    if (!usesRowLayout || !element || typeof ResizeObserver === 'undefined') {
      return;
    }

    const updateColumnCount = (containerWidth: number) => {
      const nextColumnCount = getAvailableColumnCount(
        containerWidth,
        minColumnWidth,
        maxColumnRepeat,
      );
      setAvailableColumnCount(previousColumnCount =>
        previousColumnCount === nextColumnCount ? previousColumnCount : nextColumnCount,
      );
    };

    updateColumnCount(element.clientWidth);

    const resizeObserver = new ResizeObserver(entries => {
      const [entry] = entries;
      updateColumnCount(entry?.contentRect.width ?? element.clientWidth);
    });
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [maxColumnRepeat, minColumnWidth, usesRowLayout]);

  if (!usesRowLayout) {
    return (
      <KeyValuePairsRoot
        ref={forkedRef}
        ownerState={ownerState}
        labelPosition={labelPosition}
        showDivider={showDivider}
        separateColumns={separateColumns}
        minColumnWidth={minColumnWidth}
        maxColumnRepeat={maxColumnRepeat}
        {...others}
      >
        {title && <KeyValuePairsTitle title={title} ownerState={ownerState} />}
        {items.map((item, index) => (
          <KeyValuePairItem
            key={index}
            label={item.label}
            labelPosition={labelPosition}
            value={item.value}
            keySize={keySize}
            rowSize={rowSize}
            showSideMargin={showSideMargin}
            align={item.align ?? align}
            ownerState={ownerState}
          />
        ))}
      </KeyValuePairsRoot>
    );
  }

  return (
    <KeyValuePairsSpanRoot ref={forkedRef} ownerState={ownerState} {...others}>
      {title && <KeyValuePairsTitle title={title} ownerState={ownerState} />}
      <KeyValuePairsSpanGrid
        data-column-count={availableColumnCount}
        ownerState={{ columnCount: availableColumnCount, labelPosition }}
      >
        {packedRows.flatMap((row, rowIndex) => {
          let columnStart = 1;
          const dividerSegments: Array<
            Pick<KeyValuePairsDividerOwnerState, 'columnSpan' | 'columnStart'>
          > = [];
          const itemElements = row.map(({ columnSpan, item, itemIndex }) => {
            const itemOwnerState: KeyValuePairsPositionedItemOwnerState = {
              ...ownerState,
              columnSpan,
              columnStart,
              rowIndex: rowIndex + 1,
            };
            dividerSegments.push({ columnSpan, columnStart });
            columnStart += columnSpan;

            return (
              <KeyValuePairItem
                key={`item-${itemIndex}`}
                label={item.label}
                labelPosition={labelPosition}
                value={item.value}
                keySize={keySize}
                rowSize={rowSize}
                showSideMargin={showSideMargin}
                align={item.align ?? align}
                ownerState={itemOwnerState}
              />
            );
          });

          if (!showDivider) {
            return itemElements;
          }

          if (separateColumns) {
            while (columnStart <= availableColumnCount) {
              dividerSegments.push({ columnSpan: 1, columnStart });
              columnStart += 1;
            }
          }

          const rowDividers = separateColumns ? dividerSegments : [{}];

          return [
            ...itemElements,
            ...rowDividers.map((dividerSegment, dividerIndex) => (
              <KeyValuePairsDivider
                key={`divider-${rowIndex}-${dividerIndex}`}
                aria-hidden
                ownerState={{ ...dividerSegment, rowIndex: rowIndex + 1 }}
              />
            )),
          ];
        })}
      </KeyValuePairsSpanGrid>
    </KeyValuePairsSpanRoot>
  );
}

function KeyValuePairsTitle(props: KeyValuePairsTitleProps) {
  const { ownerState, title } = props;

  return (
    <KeyValuePairsGroupTitleWrapper ownerState={ownerState}>
      {typeof title === 'string' ? (
        <KeyValuePairsGroupTitle size="lg" label={title} ownerState={ownerState} />
      ) : (
        title
      )}
    </KeyValuePairsGroupTitleWrapper>
  );
}

const KeyValuePairsRoot = styled(KeyValuePairsContainer, {
  target: keyValuePairsClasses.root,
})<{ ownerState: KeyValuePairsOwnerState }>`
  position: relative;
  // Group Title이 존재하지 않은 레이아웃인 경우 상단에 구분선 추가 (showDivider가 true일 때만)
  ${({ ownerState, theme }) => {
    const isBorderVisible = ownerState.showDivider && !ownerState.title;
    return (
      isBorderVisible &&
      css`
        border-top: 1px solid ${theme.color['border/weak/default']};
      `
    );
  }}
`;

const KeyValuePairsSpanRoot = styled('div', {
  target: keyValuePairsClasses.root,
})<{ ownerState: KeyValuePairsOwnerState }>`
  position: relative;
  overflow: hidden;

  ${({ ownerState, theme }) => {
    const isBorderVisible = ownerState.showDivider && !ownerState.title;
    return (
      isBorderVisible &&
      css`
        border-top: 1px solid ${theme.color['border/weak/default']};
      `
    );
  }}
`;

const KeyValuePairsSpanGrid = styled('div', {
  target: keyValuePairsClasses.spanGrid,
})<{ ownerState: KeyValuePairsSpanGridOwnerState }>`
  display: grid;
  grid-column-gap: ${KeyValuePairSize.COLUMN_GAP}px;
  grid-template-columns: repeat(${({ ownerState }) => ownerState.columnCount}, minmax(0, 1fr));

  ${({ ownerState }) =>
    ownerState.labelPosition === 'top' &&
    css`
      grid-row-gap: ${KeyValuePairSize.ROW_GAP}px;
    `}
`;

const KeyValuePairsGroupTitleWrapper = styled('div', {
  target: keyValuePairsClasses.groupTitleWrapper,
})<{ ownerState: KeyValuePairsOwnerState }>`
  grid-column: 1 / -1;
  padding-bottom: 12px;
  ${({ ownerState, theme }) =>
    ownerState.showDivider &&
    css`
      box-shadow: 0 1px 0 ${theme.color['border/weak/default']};
    `}
`;

const KeyValuePairsGroupTitle = styled(Label, {
  target: keyValuePairsClasses.groupTitle,
})<{ ownerState: KeyValuePairsOwnerState }>`
  padding: 4px 0;
`;

const KeyValuePairItem = styled(_KeyValuePairItem, {
  target: keyValuePairsClasses.item,
})<{ ownerState: KeyValuePairsPositionedItemOwnerState }>`
  ${({ ownerState }) =>
    ownerState.columnSpan !== undefined &&
    ownerState.columnStart !== undefined &&
    ownerState.rowIndex !== undefined &&
    css`
      grid-column: ${ownerState.columnStart} / span ${ownerState.columnSpan};
      grid-row: ${ownerState.rowIndex};
    `}
`;

const KeyValuePairsDivider = styled('div', {
  target: keyValuePairsClasses.divider,
})<{ ownerState: KeyValuePairsDividerOwnerState }>`
  grid-column: ${({ ownerState }) =>
    ownerState.columnStart === undefined || ownerState.columnSpan === undefined
      ? '1 / -1'
      : `${ownerState.columnStart} / span ${ownerState.columnSpan}`};
  grid-row: ${({ ownerState }) => ownerState.rowIndex};
  align-self: end;

  width: 100%;
  height: 1px;
  pointer-events: none;
  background: ${({ theme }) => theme.color['border/weaker/default']};
`;
